import { PropsWithChildren, useMemo } from 'react';
import { Building2, ChartColumn, Database, User, UserCog } from 'lucide-react';
import { useScope, useScopeIds } from '@nohara-dxdev/react-oidc';
import { LoadingScreen } from '@nohara-dxdev/react-core';
import { OrgEntity } from '@nohara-dxdev/oidc-core';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import NotFoundRoute from '@/app/routes/not-found';
import { AppAssignmentCheck } from '@/features/apps/components/app-assignment-check';
import { useProjectsList } from '@/features/projects/hooks/use-projects-list';
import { paths } from '@/config/paths';
import { env, IS_DEV_ENVIRONMENT } from '@/config/env';
import { useIsExternalUser } from '@/lib/rbac';

import { SidebarNav } from './sidebar-nav';

// The home portal web app shares the API host minus the `api.` subdomain:
// https://api.home-dev.build-app.jp -> https://home-dev.build-app.jp
const homeWebEndpoint = env.HOME_API_ENDPOINT.replace('://api.', '://');
// 組織作成・管理 lands on the org's own portal page; ユーザー招待・管理 is
// scoped to the app whose users are being invited.
const homeOrgUrl = (orgId: string) => `${homeWebEndpoint}/o/${orgId}`;
const homeAppUrl = (page: string) => `${homeWebEndpoint}/app/${env.APP_ID}/${page}`;

export const AppLayout = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const { orgId } = useScopeIds();
  // The org entity is already in the scope context (prepended when the org is
  // selected), so the sidebar label costs no extra request. The map is keyed by
  // scope *type*, not by id.
  const { entities } = useScope();
  const orgName = (entities.get('org') as OrgEntity | undefined)?.name;
  const { isExternal, isLoading: isRoleLoading } = useIsExternalUser();
  const { pathname } = useLocation();
  // The axios interceptor derives X-Org-Id from this same URL segment; don't fetch
  // org-scoped data until the URL carries the org, or the request 403s with no header.
  const hasUrlOrg = /\/o\/[0-9a-f-]{36}(?:\/|$)/i.test(pathname);
  // Only 社外 users render a project list in the sidebar, so only they need the
  // fetch — skip it for 社内 users.
  const { data: projectsData } = useProjectsList({ enabled: isExternal && hasUrlOrg });

  // Flatten paginated projects into プロジェクト sub-nav entries (物件一覧).
  // Only the 社外 sidebar renders these.
  const projectChildren = useMemo(
    () =>
      orgId && isExternal
        ? (projectsData?.pages.flatMap((page) => page.data) ?? []).map((project) => ({
            path: paths.org.project.getHref(orgId, project.id),
            label: project.buildingName,
          }))
        : [],
    [projectsData, orgId, isExternal]
  );

  // 社外 users get a stripped sidebar: only プロジェクト, expanded to their
  // invited 物件. 社内 users also get プロジェクト (linking to the project table
  // page) but with no 物件 sub-list — they navigate via the table, plus company /
  // master data / users through the home portal. Same shell/header either way;
  // only the item set differs.
  const items = useMemo(() => {
    if (!orgId) return [];

    const projects = {
      path: paths.org.projects.getHref(orgId),
      icon: <ChartColumn className='h-4 w-4' />,
      label: t('navigation.menu.projects'),
    };

    if (isExternal) {
      return [{ ...projects, children: projectChildren }];
    }

    return [
      projects,
      // dev-only tooling: the route only exists in local/dev builds
      ...(IS_DEV_ENVIRONMENT
        ? [
            {
              path: paths.org.devAssignRole.getHref(orgId),
              icon: <UserCog className='h-4 w-4' />,
              label: 'Dev: ロール作成・割り当て',
            },
          ]
        : []),
      {
        path: paths.org.masterData.getHref(orgId),
        icon: <Database className='h-4 w-4' />,
        label: t('masterData.nav'),
      },
      {
        path: homeOrgUrl(orgId),
        icon: <Building2 className='h-4 w-4' />,
        label: t('navigation.menu.company'),
        external: true,
      },
      {
        path: homeAppUrl('users'),
        icon: <User className='h-4 w-4' />,
        label: t('navigation.menu.users'),
        external: true,
      },
    ];
  }, [orgId, t, isExternal, projectChildren]);

  // Hold the shell until the role is known so an external user never flashes
  // the full internal menu on first paint.
  if (isRoleLoading) return <LoadingScreen />;

  return (
    <AppAssignmentCheck loadingElement={<LoadingScreen />} fallbackElement={<NotFoundRoute />}>
      <SidebarNav
        items={items}
        orgName={orgName}
        paths={{
          logout: paths.auth.logout.path,
        }}
        variant={isExternal ? 'external' : 'internal'}
      >
        {children}
      </SidebarNav>
    </AppAssignmentCheck>
  );
};
